import React, { useContext, useEffect, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faXmark } from '@fortawesome/free-solid-svg-icons';
import { useParams } from 'react-router-dom';
import { ThemeContext } from '../context/ThemeContext';
import DownloadAppButton from '../components/DownloadAppButton';
import { projects } from '../data/projects';

const gridVariants = {
    hidden: { opacity: 0 },
    visible: { opacity: 1, transition: { staggerChildren: 0.1, delayChildren: 0.08 } },
};

const itemVariants = {
    hidden: { opacity: 0, y: 18 },
    visible: { opacity: 1, y: 0, transition: { type: 'spring', stiffness: 140, damping: 18 } },
};

const ProjectDetails = () => {
    const { id } = useParams();
    const { theme, themes } = useContext(ThemeContext);
    const [selectedImage, setSelectedImage] = useState(null);
    const [showHighlights, setShowHighlights] = useState(false);
    const [galleryPortraits, setGalleryPortraits] = useState({});
    const isLight = themes[theme]?.isIconDark;
    const project = projects.find((item) => item.id === Number(id));
    const detailPage = project?.detailPage;
    const imageCards = detailPage?.imagesWithText || [];
    const screenshots = detailPage?.images || [];
    const details = detailPage?.details || [];
    const highlightSubText = themes[theme].subText;

    useEffect(() => {
        if (!selectedImage && !showHighlights) return undefined;

        const handleKeyDown = (event) => {
            if (event.key === 'Escape') {
                setSelectedImage(null);
                setShowHighlights(false);
            }
        };
        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [selectedImage, showHighlights]);

    if (!project || !detailPage) {
        return (
            <div className={`min-h-[50vh] flex items-center justify-center text-center ${themes[theme].text}`}>
                <p className="text-lg font-semibold opacity-70">Project not found</p>
            </div>
        );
    }

    const surface = isLight
        ? 'bg-black/[0.03] border-black/10 hover:border-black/25 hover:bg-black/[0.05]'
        : 'bg-white/[0.03] border-white/10 hover:border-white/25 hover:bg-white/[0.055]';
    const overviewGlass = isLight
        ? 'bg-white/[0.22] border-white/45 hover:bg-white/[0.3] hover:border-white/65'
        : 'bg-white/[0.075] border-white/[0.18] hover:bg-white/[0.11] hover:border-white/30';
    const imageFrame = isLight ? 'bg-black/5 border-black/5' : 'bg-black/35 border-white/5';
    const appIconFrame = 'bg-transparent border-white/15';

    return (
        <main className="w-full max-w-6xl mx-auto px-4 pt-5 pb-0 sm:pb-2">
            {/* Project overview */}
            <motion.section
                initial={{ opacity: 0, y: 14 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.45 }}
                className={`relative overflow-hidden rounded-2xl border p-5 sm:p-7 mb-7 sm:mb-8 shadow-lg backdrop-blur-xl shadow-[inset_0_1px_0_rgba(255,255,255,0.16),0_12px_40px_rgba(0,0,0,0.2)] transition-colors ${overviewGlass}`}
            >
                <div className="absolute -right-16 -top-20 w-56 h-56 rounded-full bg-emerald-400/10 blur-3xl pointer-events-none" />
                <div className="relative grid grid-cols-1 lg:grid-cols-[auto_minmax(0,1fr)] items-center gap-6 sm:gap-8">
                    <motion.div
                        className={`group relative mx-auto h-36 w-36 sm:h-44 sm:w-44 shrink-0 overflow-hidden rounded-3xl p-0 border shadow-md ${appIconFrame}`}
                    >
                        <img src={project.imgSrc} alt={`${project.title} app icon`} className="w-full h-full object-contain rounded-2xl" />
                        <motion.span
                            aria-hidden="true"
                            className="pointer-events-none absolute left-1/2 top-1/2 z-10 text-4xl text-slate-100 drop-shadow-[0_0_8px_rgba(255,255,255,0.95)]"
                            initial={{ x: '-50%', y: '-180%', rotate: 0, opacity: 0 }}
                            animate={{
                                x: ['-50%', '35%', '125%'],
                                y: ['-180%', '-20%', '135%'],
                                rotate: [0, 150, 320],
                                opacity: [0, 1, 0],
                            }}
                            transition={{ duration: 1.8, ease: 'easeIn', repeat: Infinity, repeatDelay: 2.2 }}
                        >
                            ✦
                        </motion.span>
                    </motion.div>

                    <div className="min-w-0 flex flex-col gap-5">
                        <div className="flex flex-col sm:flex-row items-center gap-4 sm:gap-6 text-center sm:text-left">
                            <div className="flex-1 min-w-0">
                                <p className={`text-[10px] font-mono uppercase tracking-[0.22em] mb-1.5 ${themes[theme].subText}`}>Application overview</p>
                                <h2 className={`text-2xl sm:text-3xl font-bold tracking-tight ${themes[theme].text}`}>{project.title}</h2>
                                <p className={`mt-2 text-sm sm:text-base leading-relaxed max-w-2xl ${themes[theme].subText}`}>{detailPage.logoText}</p>
                            </div>
                            {detailPage.appStoreLink && (
                                <div className="shrink-0 sm:-translate-y-2">
                                    <DownloadAppButton appStoreLink={detailPage.appStoreLink} width={152} />
                                </div>
                            )}
                        </div>

                        {details.length > 0 && (
                            <div className="relative overflow-hidden [mask-image:linear-gradient(to_right,transparent,black_5%,black_95%,transparent)]" role="region" aria-label="Project highlights">
                                <motion.div
                                    className="flex w-max"
                                    animate={{ x: ['0%', '-50%'] }}
                                    transition={{ duration: Math.max(details.length * 6, 48), ease: 'linear', repeat: Infinity }}
                                >
                                    {[0, 1].map((copy) => (
                                        <div key={copy} className="flex shrink-0 gap-3 pr-3" aria-hidden={copy === 1}>
                                            {details.map((detail, index) => {
                                                const label = detail.title || detail.description;
                                                return (
                                                    <button
                                                        key={`${copy}-${index}`}
                                                        type="button"
                                                        onClick={() => setShowHighlights(true)}
                                                        title={label}
                                                        aria-label="Open all project highlights"
                                                        aria-haspopup="dialog"
                                                        tabIndex={copy === 1 ? -1 : 0}
                                                        className={`max-w-[18rem] truncate rounded-full border px-4 py-2 text-xs font-medium shadow-sm transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-400 ${surface} ${themes[theme].text}`}
                                                    >
                                                        <span className="mr-2 text-sm text-emerald-400">✦</span>{label}
                                                    </button>
                                                );
                                            })}
                                        </div>
                                    ))}
                                </motion.div>
                            </div>
                        )}
                    </div>
                </div>
            </motion.section>

            {/* Screenshots with captions */}
            {imageCards.length > 0 && (
                <section className="mb-9 sm:mb-12">
                    <div className="mb-3 sm:mb-5 flex items-end justify-between gap-4">
                        <div>
                            <p className={`text-[10px] font-mono uppercase tracking-[0.2em] ${themes[theme].subText}`}>Inside the app</p>
                            <h2 className={`text-xl font-bold tracking-tight mt-1 ${themes[theme].text}`}>A closer look</h2>
                        </div>
                        <span className={`text-[11px] font-mono ${themes[theme].subText}`}>{String(imageCards.length).padStart(2, '0')} screens</span>
                    </div>
                    <motion.div
                        className="grid grid-cols-1 sm:grid-cols-2 gap-5"
                        variants={gridVariants}
                        initial="hidden"
                        animate="visible"
                    >
                        {imageCards.map((item, index) => (
                            <motion.button
                                key={item.imgId ?? index}
                                type="button"
                                variants={itemVariants}
                                whileHover={{ y: -3 }}
                                onClick={() => setSelectedImage(item)}
                                className={`group text-left rounded-2xl overflow-hidden border shadow-lg transition-colors duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-400 ${surface}`}
                            >
                                <div className={`relative aspect-[16/10] p-3 sm:p-4 border-b overflow-hidden ${imageFrame}`}>
                                    <img src={item.image} alt={item.title || `App screen ${index + 1}`} loading="lazy" className="w-full h-full object-contain rounded-lg transition-transform duration-500 group-hover:scale-[1.025]" />
                                    <span className="absolute top-3 left-3 rounded-full border border-white/10 bg-black/60 px-2 py-1 text-[10px] font-mono text-white/80 backdrop-blur">{String(index + 1).padStart(2, '0')}</span>
                                </div>
                                {item.description && (
                                    <div className="px-4 py-3.5">
                                        {item.title && <h3 className={`text-sm font-semibold ${themes[theme].text}`}>{item.title}</h3>}
                                        <p className={`text-xs leading-relaxed ${item.title ? 'mt-1' : ''} ${themes[theme].subText}`}>{item.description}</p>
                                    </div>
                                )}
                            </motion.button>
                        ))}
                    </motion.div>
                </section>
            )}

            {/* Gallery */}
            {screenshots.length > 0 && (
                <section className="mb-7 sm:mb-12">
                    <div className="mb-4 sm:mb-0 flex items-end justify-between gap-4">
                        <div>
                            <p className={`text-[10px] font-mono uppercase tracking-[0.2em] ${themes[theme].subText}`}>Product gallery</p>
                            <h2 className={`text-xl font-bold tracking-tight mt-1 ${themes[theme].text}`}>App screens</h2>
                        </div>
                        <span className={`text-[11px] font-mono ${themes[theme].subText}`}>{String(screenshots.length).padStart(2, '0')} images</span>
                    </div>
                    <motion.div
                        className="my-0 sm:my-8 grid items-start gap-3 sm:gap-5"
                        style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 13.2rem), 1fr))' }}
                        variants={gridVariants}
                        initial="hidden"
                        animate="visible"
                    >
                        {screenshots.map((image, index) => {
                            const imageKey = image.imgId ?? index;
                            const isPhonePortrait = galleryPortraits[imageKey] ?? image.isVertical === true;

                            return (
                                <motion.button
                                    key={imageKey}
                                    type="button"
                                    variants={itemVariants}
                                    onClick={() => setSelectedImage(image)}
                                    className={`flex w-full items-center justify-center cursor-zoom-in focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-400 ${image.galleryColumnSpan === 2 ? 'sm:col-span-2' : ''} ${image.fitWithinGalleryHeight ? 'h-[30rem]' : ''}`}
                                >
                                    <img
                                        src={image.image}
                                        alt={`${detailPage.title} screen ${index + 1}`}
                                        loading="lazy"
                                        onLoad={(event) => {
                                            const portrait = event.currentTarget.naturalHeight > event.currentTarget.naturalWidth;
                                            setGalleryPortraits((current) => current[imageKey] === portrait ? current : { ...current, [imageKey]: portrait });
                                        }}
                                        className={`w-full object-contain ${isPhonePortrait ? 'rounded-[2rem]' : 'rounded-[25px]'} ${image.fitWithinGalleryHeight ? 'h-full' : 'h-auto'}`}
                                    />
                                </motion.button>
                            );
                        })}
                    </motion.div>
                </section>
            )}

            {details.length > 0 && (
                <section className="mb-0 sm:mb-1">
                    <div className="mb-3 sm:mb-4 flex items-end justify-between gap-4">
                        <div>
                            <p className={`text-[10px] font-mono uppercase tracking-[0.2em] ${themes[theme].subText}`}>Features</p>
                            <h2 className={`text-xl font-bold tracking-tight mt-1 ${themes[theme].text}`}>Highlights</h2>
                        </div>
                    </div>
                    <div className="relative -mt-2 overflow-hidden pt-2 pb-4 [mask-image:linear-gradient(to_right,transparent,black_4%,black_96%,transparent)]" role="region" aria-label="Project highlights">
                        <motion.ul
                            className="flex w-max gap-4 pr-4"
                            animate={{ x: ['0%', '-50%'] }}
                            transition={{ duration: Math.max(details.length * 8, 64), ease: 'linear', repeat: Infinity }}
                        >
                            {[0, 1].map((copy) => details.map((detail, index) => (
                                <li key={`${copy}-${index}`} className="shrink-0">
                                    <button
                                        type="button"
                                        onClick={() => setShowHighlights(true)}
                                        aria-label="Open all project highlights"
                                        aria-haspopup="dialog"
                                        tabIndex={copy === 1 ? -1 : 0}
                                        className={`group relative flex min-h-40 w-[min(80vw,19rem)] flex-col justify-between overflow-hidden rounded-2xl border p-4 text-left shadow-lg transition-all duration-200 hover:-translate-y-1 hover:shadow-xl focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-400 ${surface}`}
                                    >
                                        <span className="absolute -right-4 -top-8 text-7xl font-black tracking-tighter text-emerald-400/[0.07] transition-transform duration-300 group-hover:scale-110">
                                            {String(index + 1).padStart(2, '0')}
                                        </span>
                                        <span className="relative flex items-center gap-2">
                                            <span className="h-1.5 w-1.5 rounded-full bg-emerald-400 shadow-[0_0_8px_rgba(52,211,153,0.45)]" />
                                            <span className={`text-[10px] font-mono uppercase tracking-[0.18em] ${highlightSubText}`}>Highlight {String(index + 1).padStart(2, '0')}</span>
                                        </span>
                                        <span className="relative mt-5 block">
                                            {detail.title && <span className={`block text-sm font-semibold leading-snug ${themes[theme].text}`}>{detail.title}</span>}
                                            {detail.description && <span className={`mt-1 block text-xs leading-relaxed ${highlightSubText}`}>{detail.description}</span>}
                                        </span>
                                    </button>
                                </li>
                            )))}
                        </motion.ul>
                    </div>
                </section>
            )}

            {/* Fullscreen image viewer */}
            <AnimatePresence>
                {selectedImage && (
                    <motion.div
                        className="fixed inset-0 z-50 flex items-center justify-center p-4 sm:p-8 bg-black/85 backdrop-blur-md cursor-zoom-out"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        onClick={() => setSelectedImage(null)}
                    >
                        <button
                            type="button"
                            onClick={() => setSelectedImage(null)}
                            className="absolute top-5 right-5 z-20 rounded-full p-2.5 text-white/70 hover:text-white hover:bg-white/10 transition-colors"
                            aria-label="Close image viewer"
                        >
                            <FontAwesomeIcon icon={faXmark} className="text-xl" />
                        </button>
                        <motion.div
                            className="relative flex flex-col max-w-5xl max-h-[88vh] rounded-2xl border border-white/15 bg-black/70 p-2 sm:p-4 shadow-2xl cursor-default overflow-hidden"
                            initial={{ scale: 0.94, opacity: 0, y: 10 }}
                            animate={{ scale: 1, opacity: 1, y: 0 }}
                            exit={{ scale: 0.94, opacity: 0, y: 10 }}
                            transition={{ type: 'spring', damping: 25, stiffness: 280 }}
                            onClick={(event) => event.stopPropagation()}
                        >
                            <img src={selectedImage.image} alt={selectedImage.title || detailPage.title} className="w-full min-h-0 flex-1 object-contain rounded-lg" />
                            {selectedImage.description && <p className="text-white/70 text-xs text-center pt-3 px-2">{selectedImage.description}</p>}
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>

            <AnimatePresence>
                {showHighlights && (
                    <motion.div
                        className="fixed inset-0 z-50 flex items-center justify-center p-4 sm:p-8 bg-black/85 backdrop-blur-md"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        onClick={() => setShowHighlights(false)}
                    >
                        <button
                            type="button"
                            onClick={() => setShowHighlights(false)}
                            className="absolute top-5 right-5 z-20 rounded-full p-2.5 text-white/70 hover:text-white hover:bg-white/10 transition-colors"
                            aria-label="Close project highlights"
                        >
                            <FontAwesomeIcon icon={faXmark} className="text-xl" />
                        </button>
                        <motion.section
                            role="dialog"
                            aria-modal="true"
                            aria-labelledby="project-highlights-title"
                            className={`relative w-full max-w-2xl max-h-[85vh] overflow-y-auto rounded-2xl border p-5 sm:p-7 shadow-2xl ${surface}`}
                            initial={{ scale: 0.94, opacity: 0, y: 10 }}
                            animate={{ scale: 1, opacity: 1, y: 0 }}
                            exit={{ scale: 0.94, opacity: 0, y: 10 }}
                            transition={{ type: 'spring', damping: 25, stiffness: 280 }}
                            onClick={(event) => event.stopPropagation()}
                        >
                            <h2 id="project-highlights-title" className={`text-xl font-bold tracking-tight mb-5 ${themes[theme].text}`}>Project highlights</h2>
                            <ul className="space-y-4">
                                {details.map((detail, index) => (
                                    <li key={`modal-${index}`} className="flex gap-3">
                                        <span className="mt-1.5 w-1.5 h-1.5 shrink-0 rounded-full bg-emerald-400" />
                                        <div>
                                            {detail.title && <h3 className={`text-sm font-semibold ${themes[theme].text}`}>{detail.title}</h3>}
                                            {detail.description && <p className={`text-sm leading-relaxed ${detail.title ? 'mt-1' : ''} ${highlightSubText}`}>{detail.description}</p>}
                                        </div>
                                    </li>
                                ))}
                            </ul>
                        </motion.section>
                    </motion.div>
                )}
            </AnimatePresence>
        </main>
    );
};

export default ProjectDetails;
